<template>
  <tr @click="openDialog()" style="cursor: pointer;" :class="{ 'selected-row': isSelected }">
    <td>{{ game.league_name }}</td>
    <td>{{ formatDate(game.pretty_date) }}</td>
    <td v-if="game.analyzer">
      <v-chip color="primary" small>
        {{ game.analyzer ? analyzers.find(a => a.id == game.analyzer)?.name : 'Ei analysoija' }}
      </v-chip>
    </td>
    <td v-else>
      <v-chip small>
        -
      </v-chip>
    </td>
    <td>{{ game.home_team_name }} - {{ game.away_team_name }}</td>
    <td>
      <v-chip small :color="getErrorColor(game.total_errors)" v-if="game.total_errors !== null && game.total_errors !== undefined">
        <span style="color: white; font-weight: bold; font-size: 15px;">
          {{ game.total_errors }}
        </span>
      </v-chip>
    </td>
    <td>
      {{ game.quality_count }} / {{ activeQCCount }}
      <v-icon color="green" v-if="fullyDone">mdi-check</v-icon>
    </td>
    <v-dialog max-width="800" v-model="dialog" @click.stop>
      <v-card>
        <v-card-title>
          <span class="text-h5"> {{ game.home_team_name }} - {{ game.away_team_name }}</span>
        </v-card-title>
        <v-card-text>
          Laadunvalvonta
        </v-card-text>
        <v-card-text>
          <div class="d-flex align-center">
            <v-select
              v-if="analyzers && analyzers.length"
              :items="analyzers"
              item-text="name"
              item-value="id"
              v-model="selectedAnalyzer"
              label="Analysoija"
              :loading="loadingAnalyzer"
              @change="setAnalyzer"
              clearable
              :disabled="!editingAnalyzer"
              class="flex-grow-1"
            />
            <v-btn icon @click="editingAnalyzer = !editingAnalyzer" class="ml-2">
              <v-icon color="primary">mdi-pencil</v-icon>
            </v-btn>
          </div>
          <span v-if="loadingItems">
            Loading...
          </span>
          <span v-else>
            <div v-for="qcItem in activeQcItems" :key="qcItem.id" class="d-flex align-center mt-4">
              <span>{{ qcItem.title }}</span>
              <div class="d-flex align-center flex-grow-1 justify-space-around">
                <div color="red" v-if="getResult(qcItem)">{{ getResult(qcItem).errors_found }} virhettä</div>
              </div>
              <v-spacer></v-spacer>
              <v-btn @click="createItem(qcItem)" v-if="!results?.some(r => r.quality_control_item_id === qcItem.id)" class="ml-2" color="primary" text>
                <v-icon class="mr-2">mdi-plus</v-icon>
                Luo kohde
              </v-btn>
              <v-btn @click="openItem(qcItem, getResult(qcItem))" v-else class="ml-2" text>
                <v-icon class="mr-2">mdi-open-in-new</v-icon>
                Avaa kohde
              </v-btn>
            </div>
          </span>
        </v-card-text>
      </v-card>
    </v-dialog>
  </tr>
</template>

<script>
import ax from '@/utils/axios/axios.js'
import { mapGetters } from 'vuex'

export default {
  name: 'QualityControlGameRow',
  props: {
    game: Object,
    qcItems: Array,
    analyzers: Array,
    dialogOpen: {
      type: Boolean,
      default: false
    },
    selectedRowId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loadingItems: false,
      results: null,
      selectedAnalyzer: null,
      loadingAnalyzer: false,
      editingAnalyzer: false
    }
  },
  computed: {
    ...mapGetters('user', ['user']),
    activeQcItems() {
      return (this.qcItems || []).filter(i => {
        if(i.archived) return false
        if (i.leagues) {
          switch(this.game.pesis_league_name) {
            case('msu'):
              return i.leagues[0] === '1'
            case('nsu'):
              return i.leagues[1] === '1'
            case('myp'):
              return i.leagues[2] === '1'
            case('nyp'):
              return i.leagues[3] === '1'
            default:
              return true
          }
        }
        return true
      })
    },
    activeQCCount() {
      return this.activeQcItems.length
    },
    fullyDone() {
      if (!this.activeQcItems || this.activeQcItems.length === 0) return false
      return this.game.quality_count === this.activeQCCount
    },
    isSelected() {
      return this.selectedRowId === this.game.id;
    },
    dialog: {
      get() {
        return this.dialogOpen
      },
      set(value) {
        if (value) {
          this.$emit('open-dialog', this.game.id)
        } else {
          this.$emit('close-dialog')
        }
      }
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.fetchAnalyzer()
        this.loadResults()
      }
    }
  },
  methods: {
    getResult(qcItem) {
      return this.results?.find(r => r.quality_control_item_id === qcItem.id)
    },
    async fetchAnalyzer() {
      this.loadingAnalyzer = true
      try {
        if (this.game.analyzer) {
          this.selectedAnalyzer = this.game.analyzer
        } else {
          const res = await ax.get(`/quality/game/${this.game.id}/analyzer`)
          this.selectedAnalyzer = res.data?.analyzer_id || null
        }
      } catch (e) {
        this.selectedAnalyzer = null
      }
      this.loadingAnalyzer = false
    },
    async setAnalyzer(analyzerId) {
      this.loadingAnalyzer = true
      try {
        await ax.post(`/quality/game/${this.game.id}/analyzer`, { analyzer_id: analyzerId })
        this.editingAnalyzer = false
      } catch (e) { /* handle error if needed */ }
      this.loadingAnalyzer = false
    },
    async createItem(qcItem) {
      try {
        const errors_found = 0;
        const quality_control_item_id = qcItem.id;
        const league_game_id = this.game.id;
        const quality_controller = this.user.email || 'controller_id_here';
        const date = new Date().toISOString().slice(0, 10);
        const percentage_checked = qcItem.percentage_checked;

        const res = await ax.post('/quality/result', {
          errors_found,
          quality_control_item_id,
          league_game_id,
          quality_controller,
          date,
          percentage_checked
        });

        if (!this.results) this.results = [];
        this.results.push(res.data);
        this.$emit('result-created', res.data);
      } catch (err) {
        console.error('Failed to create quality control result:', err);
        alert('Failed to create quality control result');
      }
    },
    openItem(item, result) {
      const filterJson = JSON.stringify({ ...item.filter, game_ids: [this.game.id], league_id: this.game.league_id, season: this.game.season_id });
      const encodedFilter = encodeURIComponent(filterJson);
      const encodedQualityControlItem = encodeURIComponent(JSON.stringify(item));
      const encodedResult = encodeURIComponent(JSON.stringify(result));
      const url = `${window.location.origin}/#/filter/nosave?quality_control=true&qc_view=${item.view}&percentage_checked=${item.percentage_checked}&analysis_phase=${item.analysis_phase}&event_key=${item.event_key}&filter=${encodedFilter}&result=${encodedResult}&item=${encodedQualityControlItem}`;

      window.open(
        url,
        'event_analysis',
        'fullscreen=yes,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
      );
    },
    openDialog() {
      this.$emit('open-dialog', this.game.id)
    },
    loadResults() {
      this.loadingItems = true
      ax.get(`/quality/game/${this.game.id}`)
        .then(res => {
          this.results = res.data
        })
        .finally(() => {
          this.loadingItems = false
        })
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${month}.${year}`
    },
    getErrorColor(errorCount) {
      if (errorCount === 0) return 'green'
      if (errorCount >= 6) return 'red'

      // For values 1-5, create a gradient from green to red
      const colors = ['green', 'light-green', 'yellow', 'orange', 'deep-orange']
      return colors[Math.min(errorCount - 1, colors.length - 1)]
    }
  }
}
</script>

<style scoped>
.selected-row {
  background-color: #bec7fe79 !important;
}

.selected-row:hover {
  background-color: #e0e0e0 !important;
}
</style>